import RightOutlined from '@ant-design/icons/RightOutlined';
import classNames from 'classnames';
import RcDropdown from 'rc-dropdown';
import useEvent from 'rc-util/lib/hooks/useEvent';
import useMergedState from 'rc-util/lib/hooks/useMergedState';
import omit from 'rc-util/lib/omit';
import * as React from 'react';
import { ConfigContext } from '../config-provider';
import Menu from '../menu';
import { OverrideProvider } from '../menu/OverrideContext';
import { NoCompactStyle } from '../space/Compact';
import getPlacements from '../_util/placements';
import genPurePanel from '../_util/PurePanel';
import { cloneElement } from '../_util/reactNode';
import warning from '../_util/warning';
import DropdownButton from './dropdown-button';
import useStyle from './style';
import theme from '../theme';
const Placements = [
    'topLeft',
    'topCenter',
    'topRight',
    'bottomLeft',
    'bottomCenter',
    'bottomRight',
    'top',
    'bottom',
];
const Dropdown = (props) => {
    const { getPopupContainer: getContextPopupContainer, getPrefixCls, direction, } = React.useContext(ConfigContext);
    // Warning for deprecated usage
    if (process.env.NODE_ENV !== 'production') {
        [
            ['visible', 'open'],
            ['onVisibleChange', 'onOpenChange'],
        ].forEach(([deprecatedName, newName]) => {
            warning(!(deprecatedName in props), 'Dropdown', `\`${deprecatedName}\` is deprecated which will be removed in next major version, please use \`${newName}\` instead.`);
        });
        warning(!('overlay' in props), 'Dropdown', '`overlay` is deprecated. Please use `menu` instead.');
    }
    const getTransitionName = () => {
        const rootPrefixCls = getPrefixCls();
        const { placement = '', transitionName } = props;
        if (transitionName !== undefined) {
            return transitionName;
        }
        if (placement.includes('top')) {
            return `${rootPrefixCls}-slide-down`;
        }
        return `${rootPrefixCls}-slide-up`;
    };
    const getPlacement = () => {
        const { placement } = props;
        if (!placement) {
            return direction === 'rtl' ? 'bottomRight' : 'bottomLeft';
        }
        if (placement.includes('Center')) {
            const newPlacement = placement.slice(0, placement.indexOf('Center'));
            warning(!placement.includes('Center'), 'Dropdown', `You are using '${placement}' placement in Dropdown, which is deprecated. Try to use '${newPlacement}' instead.`);
            return newPlacement;
        }
        return placement;
    };
    const { menu, arrow, prefixCls: customizePrefixCls, children, trigger, disabled, dropdownRender, getPopupContainer, overlayClassName, rootClassName, open, onOpenChange, 
    // Deprecated
    visible, onVisibleChange, mouseEnterDelay = 0.15, mouseLeaveDelay = 0.1, autoAdjustOverflow = true, } = props;
    if (process.env.NODE_ENV !== 'production') {
        [
            ['visible', 'open'],
            ['onVisibleChange', 'onOpenChange'],
        ].forEach(([deprecatedName, newName]) => {
            warning(!(deprecatedName in props), 'Dropdown', `\`${deprecatedName}\` is deprecated, please use \`${newName}\` instead.`);
        });
    }
    const prefixCls = getPrefixCls('dropdown', customizePrefixCls);
    const [wrapSSR, hashId] = useStyle(prefixCls);
    const { token } = theme.useToken();
    const child = React.Children.only(children);
    const dropdownTrigger = cloneElement(child, {
        className: classNames(`${prefixCls}-trigger`, {
            [`${prefixCls}-rtl`]: direction === 'rtl',
        }, child.props.className),
        disabled,
    });
    const triggerActions = disabled ? [] : trigger;
    let alignPoint;
    if (triggerActions && triggerActions.includes('contextMenu')) {
        alignPoint = true;
    }
    // =========================== Open ============================
    const [mergedOpen, setOpen] = useMergedState(false, {
        value: open !== null && open !== void 0 ? open : visible,
    });
    const onInnerOpenChange = useEvent((nextOpen) => {
        onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(nextOpen);
        onVisibleChange === null || onVisibleChange === void 0 ? void 0 : onVisibleChange(nextOpen);
        setOpen(nextOpen);
    });
    // =========================== Overlay ============================
    const overlayClassNameCustomized = classNames(overlayClassName, rootClassName, hashId, {
        [`${prefixCls}-rtl`]: direction === 'rtl',
    });
    const builtinPlacements = getPlacements({
        arrowPointAtCenter: typeof arrow === 'object' && arrow.pointAtCenter,
        autoAdjustOverflow,
        offset: token.marginXXS,
        arrowWidth: arrow ? token.sizePopupArrow : 0,
        borderRadius: token.borderRadius,
    });
    const onMenuClick = React.useCallback(() => {
        setOpen(false);
    }, []);
    const renderOverlay = () => {
        // rc-dropdown already can process the function of overlay, but we have check logic here.
        // So we need render the element to check and pass back to rc-dropdown.
        const { overlay } = props;
        let overlayNode;
        if (menu === null || menu === void 0 ? void 0 : menu.items) {
            overlayNode = React.createElement(Menu, Object.assign({}, menu));
        }
        else if (typeof overlay === 'function') {
            overlayNode = overlay();
        }
        else {
            overlayNode = overlay;
        }
        if (dropdownRender) {
            overlayNode = dropdownRender(overlayNode);
        }
        overlayNode = React.Children.only(typeof overlayNode === 'string' ? React.createElement("span", null, overlayNode) : overlayNode);
        return (React.createElement(OverrideProvider, { prefixCls: `${prefixCls}-menu`, expandIcon: React.createElement("span", { className: `${prefixCls}-menu-submenu-arrow` },
                React.createElement(RightOutlined, { className: `${prefixCls}-menu-submenu-arrow-icon` })), mode: "vertical", selectable: false, onClick: onMenuClick, validator: ({ mode }) => {
                // Warning if use other mode
                warning(!mode || mode === 'vertical', 'Dropdown', `mode="${mode}" is not supported for Dropdown's Menu.`);
            } },
            React.createElement(NoCompactStyle, null, overlayNode)));
    };
    // ============================ Render ============================
    return wrapSSR(React.createElement(RcDropdown, Object.assign({ alignPoint: alignPoint }, omit(props, ['rootClassName']), { mouseEnterDelay: mouseEnterDelay, mouseLeaveDelay: mouseLeaveDelay, visible: mergedOpen, builtinPlacements: builtinPlacements, arrow: !!arrow, overlayClassName: overlayClassNameCustomized, prefixCls: prefixCls, getPopupContainer: getPopupContainer || getContextPopupContainer, transitionName: getTransitionName(), trigger: triggerActions, overlay: renderOverlay, placement: getPlacement(), onVisibleChange: onInnerOpenChange }), dropdownTrigger));
};
Dropdown.Button = DropdownButton;
// We don't care debug panel
const PurePanel = genPurePanel(Dropdown, 'dropdown', (prefixCls) => prefixCls);
/* istanbul ignore next */
const WrapPurePanel = (props) => (React.createElement(PurePanel, Object.assign({}, props),
    React.createElement("span", null)));
Dropdown._InternalPanelDoNotUseOrYouWillBeFired = WrapPurePanel;
if (process.env.NODE_ENV !== 'production') {
    Dropdown.displayName = 'Dropdown';
}
export default Dropdown;
